import { map, reduce } from "lodash";

import { countChildren, dividerWidth } from "./utils";

export default props => {
  // Note that all values are defined as numeric pixel values.
  // Each split has the following properties:
  // -- Minimum Size -> can never go below it
  // -- Maximum Size -> can never go above it
  // -- Collapsed Size -> the size it shows when collapsed
  // -- Preferred Size -> If not -1, always use this value, do not automatically adjust (but can possibly be changed by user)
  // -- Default Size -> use as starting point, but grow/shrink proportionally to fit available space (grows like a %).

  // Make sure the height and width aren't negative
  const height = props.containerRect.height >= 0 ? props.containerRect.height : 0;
  const width = props.containerRect.width >= 0 ? props.containerRect.width : 0;

  // Convert to raw JS array for easier manipulation
  const constraintsArr = props.constraints.toArray();
  // How many children are there
  const childCount = countChildren(props.children);
  // And how much space is available
  // How large are we in the opposite direction to the split?
  const opposite = props.vertical ? height : width;
  // How much room do we have on our adjustable dimension?
  const total = Math.max(props.vertical ? width : height, 0);

  // Go through and allocate all fixed sizes immediately
  const proportionalTotal = reduce(constraintsArr, (sum, c) => sum + (c.preferredSize ? 0 : c.defaultSize), 0);
  const dividerTotal = reduce(constraintsArr, (sum, c) => sum + dividerWidth(c.hasDivider), 0);
  const spaceToAllocate = reduce(constraintsArr, (remains, c) => remains - c.preferredSize, total - dividerTotal);

  // Do an initial pass to calculate the used size of each item.
  let finalSizes = map(constraintsArr, c =>
    c.canCollapse && c.preferredSize <= c.collapseSize
      ? c.collapseSize
      : Math.min(
        c.maxSize,
        Math.max(
          c.minSize,
          c.preferredSize ? c.preferredSize : Math.round((spaceToAllocate * c.defaultSize) / proportionalTotal)
        )
      )
  );

  // Work out how much space we have currently used.
  const usedSpace = reduce(finalSizes, (sum, s) => sum + s, 0);
  let diff = total - dividerTotal - usedSpace;

  const adjustSizes = conditionCheckFunc => {
    if (diff !== 0) {
      // We have been proportional in the past, so things will be close enough, and we
      // will just make the change on the first item that will take it (aiming for the last).
      for (let i = childCount - 1; i >= 0 && diff !== 0; i--) {
        const c = constraintsArr[i];
        if (conditionCheckFunc(c)) {
          const oldSize = finalSizes[i];
          finalSizes[i] = Math.min(c.maxSize, Math.max(c.canCollapse ? c.collapseSize : c.minSize, oldSize + diff));
          diff -= finalSizes[i] - oldSize;
        }
      }
    }
  };

  if (diff > 0) {
    adjustSizes(c => !c.canCollapse && !c.preferredSize);

    adjustSizes(c => !c.canCollapse && c.preferredSize);
  }

  // When there are views can collapse
  // allocate the diff to the NOT collapsed ones
  // according to their size proportional.
  // If a collapse view is too small after allocating space,
  // collapse it to release more space
  if (diff !== 0 && constraintsArr.findIndex(c => c.canCollapse) >= 0) {
    // Sort the constraint array by size ASC
    // to process views starting from the smallest one
    const sortConstraintArr = constraintsArr
      .map((c, index) => {
        return { index: index, value: c };
      })
      .filter(cObj => cObj.value.canCollapse)
      .sort((cA, cB) => finalSizes[cA.index] - finalSizes[cB.index]);

    let nonCollapsedUsedSpace = reduce(
      finalSizes,
      (sum, s, index) => (constraintsArr[index].canCollapse && s > constraintsArr[index].collapseSize ? sum + s : sum),
      0
    );

    let origDiff = diff;

    sortConstraintArr.forEach(cObj => {
      const { value: c, index: cIndex } = cObj;
      const oldSize = finalSizes[cIndex];
      if (oldSize > c.collapseSize) {
        const spaceToAllocate = (oldSize / nonCollapsedUsedSpace) * origDiff;
        // Should collapse if view becomes too small after allocating
        if (oldSize + spaceToAllocate < c.minSize) {
          const releaseSpace = oldSize - c.collapseSize;
          origDiff += releaseSpace;
          diff += releaseSpace;
          nonCollapsedUsedSpace -= oldSize;
          finalSizes[cIndex] = c.collapseSize;
        } else {
          finalSizes[cIndex] = Math.min(c.maxSize, Math.max(c.minSize, oldSize + spaceToAllocate));
          diff -= finalSizes[cIndex] - oldSize;
        }
      }
    });
  }

  // May appear really small number like 1e-27 due to
  // dividing calculation
  // Round the diff here
  diff = Math.round(diff);

  adjustSizes(c => c.canCollapse && c.preferredSize > c.collapseSize);

  adjustSizes(c => !c.preferredSize);

  adjustSizes(c => c.preferredSize);

  // If we get here and we still have a problem, just take it off the largest view.
  if (diff !== 0) {
    const index = finalSizes.indexOf(Math.max(...finalSizes));
    finalSizes[index] += diff;
  }

  // Once we reach here we know our sizes, just need to build some structs to place the views
  let x = 0,
    y = 0,
    result = [];

  if (props.vertical) {
    for (let i = 0; i < childCount; i++) {
      result.push({ width: finalSizes[i], height: opposite, x: x, y: y });
      x += finalSizes[i] + dividerWidth(constraintsArr[i].hasDivider);
    }
  } else {
    for (let i = 0; i < childCount; i++) {
      result.push({ width: opposite, height: finalSizes[i], x: x, y: y });
      y += finalSizes[i] + dividerWidth(constraintsArr[i].hasDivider);
    }
  }

  return result;
};
